import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Search, FileSignature, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ContractStatusBadge } from "@/components/contracts/ContractStatusBadge";
import { NewContractDialog } from "@/components/contracts/NewContractDialog";
import { RenewalsBoard } from "@/components/renewals/RenewalsBoard";
import { CONTRACT_STATUS, typeLabel, fmtDate, fmtMoney, initials } from "@/lib/contracts";
import { cn, dateLocale } from "@/lib/utils";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { ContractImportDialog } from "@/components/contracts/ContractImportDialog";

const KPI = ({ label, value, accent, testid }) => (
  <div className="rounded-2xl border border-border bg-surface p-4" data-testid={testid}>
    <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
    <p className={cn("text-2xl font-semibold mt-1.5", accent)}>{value}</p>
  </div>
);

const ContractRow = ({ c, onClick, index, t, tf, exp }) => (
  <motion.button
    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.25, delay: Math.min(index * 0.04, 0.3) }}
    onClick={onClick} data-testid={`contract-row-${c.id}`}
    className="w-full text-left rounded-2xl border border-border bg-surface p-4 sm:p-5 hover:border-foreground/25 hover:bg-secondary/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2.5 flex-wrap">
          <p className="text-sm font-semibold truncate">{c.title}</p>
          <ContractStatusBadge status={c.status} />
          {c.signature && ["sent", "in_progress"].includes(c.signature.status) && (
            <span className="px-1.5 py-0.5 rounded-md border text-[10px] font-semibold text-amber-500 border-amber-500/40 bg-amber-500/10"
              data-testid={`contract-sig-progress-${c.id}`}>
              {c.signature.signed}/{c.signature.total} {tf ? tf("signés") : "signés"}
            </span>
          )}
          {c.status === "upcoming" && c.start_date && (
            <span className="px-1.5 py-0.5 rounded-md border text-[10px] font-semibold text-sky-400 border-sky-500/40 bg-sky-500/10"
              data-testid={`contract-upcoming-badge-${c.id}`}>
              {tf ? tf("Prend effet le") : "Prend effet le"} {new Date(c.start_date).toLocaleDateString(dateLocale())}
            </span>
          )}
          {c.status === "active" && c.end_date && (
            <span className="px-1.5 py-0.5 rounded-md border text-[10px] font-semibold text-emerald-400 border-emerald-500/40 bg-emerald-500/10"
              data-testid={`contract-days-left-${c.id}`}>
              {Math.max(0, Math.ceil((new Date(c.end_date) - Date.now()) / 86400000))}{tf ? tf("j") : "j"} {tf ? tf("restants") : "restants"}
            </span>
          )}
          {exp && tf && (
            <span className={cn("px-1.5 py-0.5 rounded-md border text-[10px] font-semibold",
              exp.expired ? "text-red-500 border-red-500/40 bg-red-500/10" : "text-amber-500 border-amber-500/40 bg-amber-500/10")}
              data-testid={`contract-expiring-badge-${c.id}`}>
              {exp.expired ? tf("Offre expirée") : `${tf("Offre expire dans")} ${Math.max(exp.remaining_days, 0)}${tf("j")}`}
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1.5 flex-wrap">
          <span>{typeLabel(c.contract_type)}</span>
          {c.player_name && <><span className="opacity-40">·</span><span>{c.player_name}</span></>}
          {c.counterparty && <><span className="opacity-40">·</span><span>{c.counterparty}</span></>}
          {c.amount_cents != null && <><span className="opacity-40">·</span><span>{fmtMoney(c.amount_cents, c.currency)}</span></>}
        </p>
      </div>
      <div className="flex items-center gap-4 shrink-0">
        {c.signatories_preview?.length > 0 && (
          <div className="flex -space-x-2">
            {c.signatories_preview.map((s, i) => (
              <div key={`${s.name}-${i}`} title={s.name}
                className={cn("h-7 w-7 rounded-full border-2 border-background flex items-center justify-center text-[10px] font-semibold",
                  s.status === "signed" ? "bg-green-500/20 text-green-400" : "bg-secondary/50 text-muted-foreground")}>
                {initials(s.name)}
              </div>
            ))}
          </div>
        )}
        <div className="w-24">
          <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
            <span>{t("contracts.signatures")}</span><span>{c.progress?.signed}/{c.progress?.total}</span>
          </div>
          <div className="h-1.5 rounded-full bg-secondary/50 overflow-hidden">
            <div className={cn("h-full rounded-full transition-[width] duration-500", c.progress?.percent === 100 ? "bg-green-500" : "bg-amber-500")}
              style={{ width: `${c.progress?.percent || 0}%` }} />
          </div>
        </div>
        <span className="text-xs text-muted-foreground hidden md:block w-20 text-right">{fmtDate(c.updated_at)}</span>
      </div>
    </div>
  </motion.button>
);

export default function Contracts() {
  const navigate = useNavigate();
  const { t, tf } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [importCtx, setImportCtx] = useState(null);
  const [chooserStart, setChooserStart] = useState("choose");
  const keepImportCtx = useRef(false);
  const [initialPlayerId, setInitialPlayerId] = useState("");
  const status = searchParams.get("status") || "all";
  const tab = searchParams.get("tab") === "renewals" ? "renewals" : "contracts";

  const setStatus = (v) => {
    const next = new URLSearchParams(searchParams);
    if (v === "all") next.delete("status"); else next.set("status", v);
    setSearchParams(next);
  };

  const setTab = (t) => {
    const next = new URLSearchParams();
    if (t === "renewals") next.set("tab", "renewals");
    setSearchParams(next);
  };

  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setInitialPlayerId(searchParams.get("player") || "");
      setCreateOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      next.delete("player");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/contracts");
      setData(r.data);
    } catch (e) { console.error("contracts load", e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const [expiring, setExpiring] = useState({});
  useEffect(() => {
    api.get("/negotiations/expiring?days=7").then((r) => {
      const m = {};
      (r.data?.items || []).forEach((i) => { m[i.contract_id] = i; });
      setExpiring(m);
    }).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    let rows = data?.contracts || [];
    if (status === "expiring") rows = rows.filter((c) => expiring[c.id]);
    else if (status === "negotiation_seg") rows = rows.filter((c) => c.negotiation_active);
    else if (status === "signature_seg") rows = rows.filter((c) => ["pending_signature", "partially_signed"].includes(c.status));
    else if (status !== "all") rows = rows.filter((c) => c.status === status);
    if (q.trim()) {
      const ql = q.trim().toLowerCase();
      rows = rows.filter((c) => [c.title, c.player_name, c.counterparty].some((v) => (v || "").toLowerCase().includes(ql)));
    }
    return rows;
  }, [data, status, q, expiring]);

  const s = data?.summary || {};

  return (
    <div className="w-full max-w-[1180px] mx-auto px-4 sm:px-6 pt-6 pb-12 space-y-5" data-testid="contracts-page">
      <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-[-0.02em]">{t("contracts.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("contracts.subtitle")}</p>
        </div>
        {tab === "contracts" && (
          <Button onClick={() => setChooserOpen(true)} data-testid="new-contract-btn">
            <Plus className="h-4 w-4 mr-1.5" /> {t("contracts.new")}
          </Button>
        )}
      </header>

      <div className="flex items-center gap-1 border-b border-border" data-testid="contracts-tabs">
        {[{ id: "contracts", label: t("contracts.tab.contracts") }, { id: "renewals", label: t("contracts.tab.renewals") }].map((t2) => (
          <button key={t2.id} onClick={() => setTab(t2.id)} data-testid={`contracts-tab-${t2.id}`}
            className={cn("relative px-4 py-2.5 text-sm font-medium transition-colors",
              tab === t2.id ? "text-foreground" : "text-muted-foreground hover:text-foreground")}>
            {t2.label}
            {tab === t2.id && (
              <motion.span layoutId="contracts-tab-underline"
                className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-athlynx" />
            )}
          </button>
        ))}
      </div>

      {tab === "renewals" ? <RenewalsBoard /> : (<>
      <div className="flex flex-wrap gap-2" data-testid="contracts-segments">
        {[["all", tf("Tous")], ["negotiation_seg", tf("Négociation")], ["signature_seg", tf("En signature")],
          ["active", tf("Actifs")], ["expiring", tf("Expirent bientôt")]].map(([v, lbl]) => (
          <button key={v} type="button" onClick={() => setStatus(v)} data-testid={`contracts-seg-${v}`}
            className={cn("px-3.5 py-1.5 rounded-full border text-xs font-medium transition-colors",
              status === v ? "bg-foreground text-background border-foreground"
                : "border-border dark:border-border text-muted-foreground hover:text-foreground")}>
            {lbl}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPI label={t("common.total")} value={s.total ?? "—"} testid="kpi-total" />
        <KPI label={t("contracts.kpi.inSignature")} value={s.in_signature ?? "—"} accent="text-amber-400" testid="kpi-in-signature" />
        <KPI label={t("contracts.kpi.signed")} value={(s.signed ?? 0) + (s.active ?? 0)} accent="text-green-500" testid="kpi-signed" />
        <KPI label={t("contracts.kpi.drafts")} value={s.draft ?? "—"} testid="kpi-drafts" />
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder={t("contracts.search")} value={q}
            onChange={(e) => setQ(e.target.value)} data-testid="contracts-search-input" />
        </div>
        <Select value={status} onValueChange={(v) => {
          const next = new URLSearchParams(searchParams);
          if (v === "all") next.delete("status"); else next.set("status", v);
          setSearchParams(next);
        }}>
          <SelectTrigger className="sm:w-[230px]" data-testid="contracts-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("contracts.allStatuses")}</SelectItem>
            <SelectItem value="expiring" data-testid="contracts-filter-expiring">{tf("Offre expire bientôt")}</SelectItem>
            {Object.entries(CONTRACT_STATUS).map(([id, m]) => <SelectItem key={id} value={id}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-14 text-center" data-testid="contracts-empty">
          <FileSignature className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
          <p className="text-sm font-medium">{(data?.summary?.total || 0) === 0 ? t("contracts.emptyNone") : t("contracts.emptyNoMatch")}</p>
          <p className="text-xs text-muted-foreground mt-1 mb-4">{t("contracts.emptyHint")}</p>
          <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="empty-new-contract-btn"><Plus className="h-4 w-4 mr-1.5" /> {t("contracts.new")}</Button>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((c, i) => <ContractRow key={c.id} c={c} index={i} t={t} tf={tf} exp={expiring[c.id]} onClick={() => navigate(`/contracts/${c.id}`)} />)}
        </div>
      )}
      </>)}

      <NewContractDialog open={createOpen}
        onOpenChange={(o) => { setCreateOpen(o); if (!o) { if (keepImportCtx.current) keepImportCtx.current = false; else setImportCtx(null); } }}
        initialPlayerId={initialPlayerId}
        extraction={importCtx?.extraction || null} importFile={importCtx?.file || null}
        onBack={importCtx ? () => { keepImportCtx.current = true; setChooserStart("import"); setChooserOpen(true); } : null}
        onImportRequest={() => { setCreateOpen(false); setImportCtx(null); setChooserStart("import"); setChooserOpen(true); }}
        onSaved={(c) => navigate(`/contracts/${c.id}`)} />
      <ContractImportDialog open={chooserOpen}
        onOpenChange={(o) => { setChooserOpen(o); if (!o) setChooserStart("choose"); }}
        startStep={chooserStart} preservedFile={importCtx?.file || null}
        onManual={() => { setChooserOpen(false); setImportCtx(null); setCreateOpen(true); }}
        onExtracted={(extraction, file) => { setImportCtx({ extraction, file }); setChooserOpen(false); setCreateOpen(true); }} />
    </div>
  );
}
